import styled from "@emotion/styled";
import { SketchPicker } from "react-color";
import {
  Icon,
  Popover,
  Theme,
  Typography,
  useTheme,
} from "@yuno/design-system-web";
import { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../../../../context/theme-context";
import { useTranslation } from "react-i18next";

const POPOVER_STYLE = {
  marginLeft: "-250px",
  zIndex: "1000000000",
};

const Container = styled.div<{ theme?: Theme; focused: boolean }>`
  cursor: pointer;
  p.label {
    ${({ theme }) => theme.typography.tiny.regular};
    color: ${({ theme, focused }) => focused && theme.palette.vivid.brand};
    margin-bottom: 4px;
  }
`;

const Input = styled.div<{
  theme?: Theme;
  focused?: boolean;
  noMargin?: boolean;
}>`
  border: 1px solid
    ${({ theme, focused }) =>
      focused ? theme.palette.vivid.brand : theme.palette.grey[2]};
  padding: 8px 12px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  margin-bottom: ${({ noMargin }) => (noMargin ? "0px" : "24px")};

  .color-label {
    ${({ theme }) => theme.typography.small.regular};
    margin-left: 16px;
    text-transform: uppercase;
  }
`;

const PopoverWrapper = styled.div<{ theme?: Theme }>`
  box-shadow: -9px 9px 22px 0px rgba(0, 0, 0, 0.17);
  border-radius: 4px;

  & .sketch-picker {
    box-shadow: none !important;
  }

  & .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;

    p {
      ${({ theme }) => theme.typography.tiny.regular};
    }

    svg {
      cursor: pointer;
    }
  }
`;

const Wrapper = styled.div``;

export const ColorSelector = ({
  color,
  label = "",
  onChange,
  noMargin = false,
  i18n = null,
}) => {
  const theme: Theme = useTheme();
  const [selectedColor, setSelectedColor] = useState(color);
  const [showSelector, setShowSelector] = useState(null);
  const { setUserAction } = useContext(ThemeContext);
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedColor(color);
  }, [color]);

  const handleShowSelector = (evt) => {
    setShowSelector(evt.currentTarget);
  };

  const closeSelector = () => {
    setShowSelector(null);
  };

  const handleChangeColor = (color) => {
    setUserAction(true);
    setSelectedColor(color.hex);
    onChange(color.hex);
  };

  const iconBorder = {
    border: `1px solid  ${
      selectedColor.toUpperCase().replace("#", "") >= "FCFCFF"
        ? theme.palette.grey[2]
        : "transparent"
    }`,
  };

  return (
    <Wrapper>
      <Popover
        onClose={closeSelector}
        open={showSelector}
        anchorEl={showSelector}
        hideBackdrop
        style={POPOVER_STYLE}
        PaperProps={{ style: { overflow: "unset" } }}
      >
        <PopoverWrapper>
          <div className="header">
            <Typography>{t('styles.fields.selectColor')}</Typography>
            <Icon
              name="X"
              weight="thin"
              size="16px"
              color={theme.palette.grey[5]}
              onClick={closeSelector}
            />
          </div>
          <SketchPicker color={selectedColor} onChange={handleChangeColor} />
        </PopoverWrapper>
      </Popover>
      <Container onClick={handleShowSelector} focused={Boolean(showSelector)}>
        <Typography className="label">{i18n ? t(i18n) : label}</Typography>
        <Input focused={Boolean(showSelector)} noMargin={noMargin}>
          <Icon
            name={"Square"}
            weight="fill"
            size={"24px"}
            style={iconBorder}
            color={selectedColor}
          />
          <Typography className="color-label">{selectedColor}</Typography>
        </Input>
      </Container>
    </Wrapper>
  );
};
